"use client";

import { ChevronDown, Menu, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

import { AuthLink } from "@/components/shared/auth-link";
import { ModelSearch } from "@/components/shared/model-search";
import { Button } from "@/lib/components/button";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@/lib/components/navigation-menu";
import { useAppConfig } from "@/lib/config";
import Logo from "@/lib/icons/Logo";
import { cn } from "@/lib/utils";

import { ThemeToggle } from "./theme-toggle";

import type { Route } from "next";
import type { ReactNode } from "react";

function ListItem({
	title,
	href,
	children,
	external,
}: {
	title: string;
	href: string;
	children: ReactNode;
	external?: boolean;
}) {
	return (
		<li>
			<NavigationMenuLink asChild>
				{external ? (
					<a
						href={href}
						target="_blank"
						rel="noopener noreferrer"
						className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
					>
						<div className="text-sm font-medium leading-none">{title}</div>
						<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
							{children}
						</p>
					</a>
				) : (
					<Link
						href={href as Route}
						prefetch={true}
						className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
					>
						<div className="text-sm font-medium leading-none">{title}</div>
						<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
							{children}
						</p>
					</Link>
				)}
			</NavigationMenuLink>
		</li>
	);
}

export const Navbar = ({ children }: { children?: React.ReactNode }) => {
	const config = useAppConfig();

	const menuItems: Array<{ name: string; href: string; external?: boolean }> = [
		{ name: "Pricing", href: "/#pricing" },
		{ name: "Docs", href: config.docsUrl ?? "", external: true },
		{ name: "Models", href: "/models" },
	];

	const chatItem: { name: string; href: string; external?: boolean } = {
		name: "Chat",
		href:
			process.env.NODE_ENV === "development"
				? "http://localhost:3003"
				: "https://chat.llmhub.andaihub.ai",
		external: true,
	};

	const resourcesItems = [
		{ name: "Blog", href: "/blog" },
		{ name: "Providers", href: "/providers" },
		{ name: "Changelog", href: "/changelog" },
		{ name: "Referral Program", href: "/referrals" },
		{ name: "Docs", href: config.docsUrl ?? "", external: true },
		{ name: "Model Timeline", href: "/timeline" },
		{ name: "Compare", href: "/models/compare" },
		{ name: "Contact Us", href: "mailto:contact@llmhub.andaihub.ai" },
	];

	const resourcesLinks: Array<{
		title: string;
		href: string | Route;
		description: string;
		external?: boolean;
	}> = [
		{
			title: "Blog",
			href: "/blog",
			description: "Product updates, tutorials, benchmarks, and announcements.",
		},
		{
			title: "Docs",
			href: config.docsUrl ?? "#",
			description:
				"Guides, API reference, and examples to get you shipping fast.",
			external: true,
		},
		{
			title: "Compare",
			href: "/models/compare",
			description: "Compare models side by side",
		},
		{
			title: "Changelog",
			href: "/changelog",
			description: "What’s new in AndAI LLM Hub across releases.",
		},
		{
			title: "Referral Program",
			href: "/referrals",
			description: "Earn 1% of LLM spending.",
		},
		{
			title: "Providers",
			href: "/providers",
			description: "Connect and manage your provider API keys.",
		},
		{
			title: "Model Timeline",
			href: "/timeline",
			description: "Track the release history of all models.",
		},
	];

	const [menuState, setMenuState] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 50);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (
		<header>
			<nav
				data-state={menuState && "active"}
				className="fixed z-20 w-full px-2 group"
			>
				<div
					className={cn(
						"mt-2 mx-auto max-w-7xl px-6 transition-all duration-300",
						isScrolled &&
							"bg-background/50 max-w-7xl rounded-2xl border backdrop-blur-lg lg:px-5",
					)}
				>
					<div className="relative flex flex-wrap items-center justify-between gap-6 py-3 lg:gap-0 lg:py-4">
						{/* Logo and Title */}
						<div className="flex w-full justify-between lg:w-auto">
							<Link
								href="/"
								aria-label="home"
								className="flex items-center space-x-2"
								prefetch={true}
							>
								<Logo className="h-8 w-8 rounded-full text-black dark:text-white" />
								<span className="text-xl font-bold tracking-tight text-zinc-900 dark:text-white">
									AndAI LLM Hub
								</span>
							</Link>

							<button
								onClick={() => setMenuState(!menuState)}
								aria-label={menuState ? "Close Menu" : "Open Menu"}
								className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden"
							>
								<Menu className="group-data-[state=active]:scale-0 group-data-[state=active]:opacity-0 size-6 duration-200" />
								<X className="absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 group-data-[state=active]:rotate-0 group-data-[state=active]:scale-100 group-data-[state=active]:opacity-100 duration-200" />
							</button>
						</div>

						<div className="m-auto hidden items-center gap-4 lg:flex">
							<div className="w-[140px] lg:w-[160px]">
								<ModelSearch />
							</div>
							<NavigationMenu viewport={false}>
								<NavigationMenuList className="flex gap-2 text-sm">
									{menuItems.map((item, index) => (
										<NavigationMenuItem key={index}>
											<NavigationMenuLink asChild>
												<Link
													href={item.href as Route}
													className="text-muted-foreground hover:text-accent-foreground block duration-150 px-4 py-2"
													prefetch={true}
												>
													{item.name}
												</Link>
											</NavigationMenuLink>
										</NavigationMenuItem>
									))}

									<NavigationMenuItem>
										<NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground px-4 py-2">
											Resources
										</NavigationMenuTrigger>
										<NavigationMenuContent>
											<ul className="grid gap-3 p-6 md:w-[480px] lg:w-[640px] lg:grid-cols-[.8fr_1fr]">
												<li className="row-span-3">
													<NavigationMenuLink asChild>
														<a
															className="flex h-full w-full select-none flex-col justify-end rounded-md bg-linear-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md"
															href={
																config.docsUrl
																	? `${config.docsUrl}/quick-start`
																	: "#"
															}
															target={config.docsUrl ? "_blank" : undefined}
															rel={
																config.docsUrl
																	? "noopener noreferrer"
																	: undefined
															}
														>
															<div className="mb-2 mt-4 text-lg font-medium">
																Quick Start
															</div>
															<p className="text-sm leading-tight text-muted-foreground">
																Get started in minutes. Install, configure
																providers, and call the API.
															</p>
														</a>
													</NavigationMenuLink>
												</li>
												{resourcesLinks.map((link) => (
													<ListItem
														key={link.title}
														title={link.title}
														href={link.href}
														external={link.external}
													>
														{link.description}
													</ListItem>
												))}
											</ul>
										</NavigationMenuContent>
									</NavigationMenuItem>

									<NavigationMenuItem>
										<NavigationMenuLink asChild>
											<a
												href={chatItem.href}
												target={chatItem.external ? "_blank" : undefined}
												rel={
													chatItem.external ? "noopener noreferrer" : undefined
												}
												className="text-muted-foreground hover:text-accent-foreground block duration-150 px-4 py-2"
											>
												{chatItem.name}
											</a>
										</NavigationMenuLink>
									</NavigationMenuItem>
								</NavigationMenuList>
							</NavigationMenu>
						</div>

						<div className="bg-background group-data-[state=active]:block lg:group-data-[state=active]:flex mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 rounded-3xl border p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent">
							<div className="lg:hidden">
								<ul className="space-y-6 text-base">
									{menuItems.map((item, index) => (
										<li key={index}>
											{item.external ? (
												<a
													href={item.href}
													target="_blank"
													rel="noopener noreferrer"
													className="text-muted-foreground hover:text-accent-foreground block duration-150"
												>
													{item.name}
												</a>
											) : (
												<Link
													href={item.href as Route}
													className="text-muted-foreground hover:text-accent-foreground block duration-150"
													prefetch={true}
												>
													{item.name}
												</Link>
											)}
										</li>
									))}

									<li>
										<a
											href={chatItem.href}
											target={chatItem.external ? "_blank" : undefined}
											rel={
												chatItem.external ? "noopener noreferrer" : undefined
											}
											className="text-muted-foreground hover:text-accent-foreground block duration-150"
										>
											{chatItem.name}
										</a>
									</li>

									<li className="space-y-2">
										<button
											type="button"
											onClick={() => setIsMobileResourcesOpen((prev) => !prev)}
											className="flex w-full items-center justify-between gap-2 text-left"
											aria-expanded={isMobileResourcesOpen}
											aria-controls="mobile-resources-menu"
										>
											<span className="text-muted-foreground text-sm font-medium">
												Resources
											</span>
											<ChevronDown
												className={cn(
													"h-4 w-4 text-muted-foreground transition-transform duration-200",
													isMobileResourcesOpen && "rotate-180",
												)}
											/>
										</button>
										{isMobileResourcesOpen ? (
											<ul
												id="mobile-resources-menu"
												className="space-y-3 pl-4 pt-1"
											>
												{resourcesItems.map((item, index) => (
													<li key={index}>
														<Link
															href={item.href as Route}
															className="text-muted-foreground hover:text-accent-foreground block duration-150"
															prefetch={true}
														>
															{item.name}
														</Link>
													</li>
												))}
											</ul>
										) : null}
									</li>
								</ul>
							</div>

							<div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit items-center">
								{children}

								<Button
									asChild
									className={cn(
										"bg-zinc-900 dark:bg-white text-white dark:text-black hover:bg-zinc-700 dark:hover:bg-zinc-200 font-medium w-full md:w-fit",
									)}
								>
									<AuthLink href="/signup">Get Started</AuthLink>
								</Button>
								<ThemeToggle />
							</div>
						</div>
					</div>
				</div>
			</nav>
		</header>
	);
};
